import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  hackerChecklistService,
  HackerChecklist
} from '../../lib/hackerChecklistsService';

export const HackerChecklistsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();

  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('');
  const [checklists, setChecklists] = useState<HackerChecklist[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  const isNewSession = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('newSession') === 'true';
  }, [location.search]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/signin');
        return;
      }
      const displayName = user.displayName || user.email?.split('@')[0] || 'Hacker';
      setUserName(displayName);
      localStorage.setItem('rawpa_user_name', displayName);
    });
    return () => unsubscribe();
  }, [auth, navigate]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await hackerChecklistService.getChecklists({ status: 'Published' });
        setChecklists(data);
      } catch (err) {
        console.error('Failed to load hacker checklists', err);
        setError('Failed to load hacker checklists. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const visibleChecklists = useMemo(() => {
    if (categoryFilter === 'all') return checklists;
    return checklists.filter((checklist) => (checklist.category || 'general') === categoryFilter);
  }, [checklists, categoryFilter]);

  const categories = useMemo(() => {
    const set = new Set<string>();
    checklists.forEach((checklist) => set.add(checklist.category || 'general'));
    return ['all', ...Array.from(set)];
  }, [checklists]);

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-black">Loading hacker checklists…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4">
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
            </div>
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 pt-[160px] pb-12">
        <div className="bg-white rounded-[20px] p-8 shadow-lg">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
                Hacker Checklists
              </h1>
              <p className="text-sm text-gray-700 max-w-2xl">
                Curated playbooks for recon, CTF, and assessment workflows. Select a checklist to begin tracking your progress.
              </p>
              {isNewSession && (
                <p className="text-xs text-blue-600 mt-2">
                  New session mode enabled. Pick a checklist to start with a clean slate.
                </p>
              )}
            </div>
            <div className="flex gap-2 flex-wrap justify-end">
              <select
                className="border rounded-lg h-10 px-3 text-sm"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category === 'all' ? 'All categories' : category}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mt-4">
              <p>{error}</p>
            </div>
          )}

          {visibleChecklists.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <p>No checklists available for this category yet.</p>
              <p className="text-sm mt-2">Check back soon as admins publish new content.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mt-6">
              {visibleChecklists.map((checklist) => (
                <div key={checklist.id} className="border border-[#E7E7E7] rounded-[20px] p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="font-['Space_Grotesk'] font-semibold text-lg text-black">
                        {checklist.title}
                      </h2>
                      {checklist.difficulty && (
                        <span className="inline-block text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                          {checklist.difficulty}
                        </span>
                      )}
                      <span className="inline-block text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                        {(checklist.category || 'general').toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 line-clamp-3 min-h-[60px]">
                      {checklist.description || 'No description provided by the admin.'}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span>{checklist.sections.length} sections</span>
                      <span>
                        {checklist.sections.reduce((total, section) => total + section.items.length, 0)} items
                      </span>
                    </div>
                    {checklist.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 text-xs text-gray-600">
                        {checklist.tags.map((tag) => (
                          <span key={tag} className="bg-gray-100 rounded-full px-2 py-1">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button
                    className="mt-6 bg-[#daed6e] text-black hover:bg-[#c5d85c]"
                    onClick={() => navigate(`/hacker-checklists/${checklist.id}${isNewSession ? '?newSession=true' : ''}`)}
                  >
                    Start Checklist
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HackerChecklistsPage;

